/**
 * The three distributions (spec §9.2): hosted page, iframe embed, deep
 * links with product/slot preselect. Resolution is a pure function of the
 * URL and framedness. It NEVER throws: a malformed query degrades to a
 * plain hosted storefront rather than blanking the buyer's page.
 *
 * In embedded mode the postMessage bridge (B31a) may later override or
 * complete this context (site key, preselect, theme hints); URL params are
 * the shared bootstrap for all three distributions.
 */

export type Distribution = "hosted" | "embedded";

/**
 * B517 — how an EMBEDDED frame presents itself (the loader's words): the
 * inline product `list` (cards only; a press opens the dialog) or the booking
 * `dialog`. Absent = the pre-B517 inline flow.
 */
export type Presentation = "list" | "dialog";

export interface Preselect {
  readonly productId?: string;
  readonly slotId?: string;
}

export interface StorefrontContext {
  readonly distribution: Distribution;
  readonly tenantId?: string;
  /** Publishable per-channel site key (§9.2 tenant configuration). */
  readonly siteKey?: string;
  /** B181 — the channel the resolve answered (or a bridge supplied); absent = the baked default. */
  readonly channelId?: string;
  readonly preselect: Preselect;
  /** B517 — framed pages only; a hosted page's chrome is never URL-strippable. */
  readonly presentation?: Presentation;
}

export interface ContextInput {
  readonly href: string;
  /** window.self !== window.top at the call site. */
  readonly framed: boolean;
}

function param(params: URLSearchParams, name: string): string | undefined {
  const value = params.get(name);
  return value === null || value === "" ? undefined : value;
}

export function resolveContext(input: ContextInput): StorefrontContext {
  let params: URLSearchParams;
  try {
    params = new URL(input.href).searchParams;
  } catch {
    params = new URLSearchParams();
  }
  const tenantId = param(params, "tenant");
  const siteKey = param(params, "siteKey");
  const productId = param(params, "product");
  const slotId = param(params, "slot");
  const asked = param(params, "presentation");
  const presentation: Presentation | undefined =
    input.framed && (asked === "list" || asked === "dialog") ? asked : undefined;
  return {
    distribution: input.framed ? "embedded" : "hosted",
    ...(presentation !== undefined ? { presentation } : {}),
    ...(tenantId !== undefined ? { tenantId } : {}),
    ...(siteKey !== undefined ? { siteKey } : {}),
    preselect: {
      ...(productId !== undefined ? { productId } : {}),
      ...(slotId !== undefined ? { slotId } : {}),
    },
  };
}
